// frontend/src/charts/ChartGlyph.jsx
// Chart-type glyphs (design decision D9): each type drawn as a tiny real chart
// in the validated series colours, the way Power BI's visual picker and
// Tableau's Show Me do, instead of generic outline icons. 34 x 24, decorative
// (aria-hidden); the button that holds one carries the name. `muted` greys a
// type the current fields can't draw.
import React from 'react';
import { cn } from '@/lib/utils';

const S1 = 'var(--series-1)';
const S2 = 'var(--series-2)';
const S3 = 'var(--series-3)';

const SHAPES = {
  bar: (
    <>
      <rect x="3" y="9" width="5" height="13" rx="1.5" fill={S1} />
      <rect x="11" y="4" width="5" height="18" rx="1.5" fill={S1} />
      <rect x="19" y="12" width="5" height="10" rx="1.5" fill={S1} />
      <rect x="27" y="15" width="5" height="7" rx="1.5" fill={S1} />
    </>
  ),
  stackedBar: (
    <>
      <rect x="4" y="12" width="6" height="10" rx="1.2" fill={S1} />
      <rect x="4" y="6" width="6" height="5" rx="1.2" fill={S2} />
      <rect x="14" y="9" width="6" height="13" rx="1.2" fill={S1} />
      <rect x="14" y="2" width="6" height="6" rx="1.2" fill={S2} />
      <rect x="24" y="15" width="6" height="7" rx="1.2" fill={S1} />
      <rect x="24" y="10" width="6" height="4" rx="1.2" fill={S2} />
    </>
  ),
  horizontalBar: (
    <>
      <rect x="3" y="3" width="26" height="5" rx="1.5" fill={S1} />
      <rect x="3" y="10" width="18" height="5" rx="1.5" fill={S1} />
      <rect x="3" y="17" width="10" height="5" rx="1.5" fill={S1} />
    </>
  ),
  line: (
    <>
      <path d="M3 18 L10 12 L16 15 L23 6 L31 9" fill="none" stroke={S1} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="31" cy="9" r="2.2" fill={S1} />
    </>
  ),
  area: (
    <>
      <path d="M3 18 L10 11 L17 14 L24 6 L31 9 L31 22 L3 22Z" fill={S1} opacity="0.28" />
      <path d="M3 18 L10 11 L17 14 L24 6 L31 9" fill="none" stroke={S1} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  doughnut: (
    <>
      <circle cx="17" cy="12" r="8" fill="none" stroke={S3} strokeWidth="4.5" strokeDasharray="14 60" strokeDashoffset="-36" />
      <circle cx="17" cy="12" r="8" fill="none" stroke={S2} strokeWidth="4.5" strokeDasharray="13 60" strokeDashoffset="-22" />
      <circle cx="17" cy="12" r="8" fill="none" stroke={S1} strokeWidth="4.5" strokeDasharray="21 60" />
    </>
  ),
  card: (
    <text x="17" y="16.5" textAnchor="middle" fontSize="12.5" fontWeight="700" fill="var(--ui-text)"
      style={{ fontFamily: "'Geist Variable', system-ui, sans-serif" }}>1.4k</text>
  ),
  table: (
    <>
      <rect x="4" y="3" width="26" height="4" rx="1.5" fill="var(--ui-text-muted)" />
      <rect x="4" y="10" width="26" height="3" rx="1.5" fill="var(--ui-chip)" />
      <rect x="4" y="15.5" width="26" height="3" rx="1.5" fill="var(--ui-chip)" />
    </>
  ),
  // 7e-3: column headers across the top, row headers down the left, cells, and the totals in the series colour.
  matrix: (
    <>
      <rect x="12" y="3" width="8" height="3.5" rx="1.2" fill="var(--ui-text-muted)" />
      <rect x="22" y="3" width="8" height="3.5" rx="1.2" fill={S1} opacity="0.75" />
      <rect x="4" y="9" width="6" height="3" rx="1.2" fill="var(--ui-text-muted)" />
      <rect x="4" y="14" width="6" height="3" rx="1.2" fill="var(--ui-text-muted)" />
      <rect x="12" y="9" width="8" height="3" rx="1.2" fill="var(--ui-chip)" />
      <rect x="12" y="14" width="8" height="3" rx="1.2" fill="var(--ui-chip)" />
      <rect x="22" y="9" width="8" height="3" rx="1.2" fill={S1} opacity="0.45" />
      <rect x="22" y="14" width="8" height="3" rx="1.2" fill={S1} opacity="0.45" />
      <rect x="4" y="19" width="26" height="3" rx="1.2" fill={S1} opacity="0.75" />
    </>
  ),
};

/** The chart types that have a glyph (the builder's v1 set). */
export const GLYPH_TYPES = Object.keys(SHAPES);

export default function ChartGlyph({ type, muted = false, className }) {
  const shape = SHAPES[type];
  if (!shape) return null;
  return (
    <svg width="34" height="24" viewBox="0 0 34 24" aria-hidden="true" focusable="false"
      className={cn('shrink-0 transition-[filter,opacity]', muted && 'opacity-30 grayscale', className)}>
      {shape}
    </svg>
  );
}